import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Search,
  Users,
  Calculator,
  CreditCard,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Heart,
  ChevronRight,
  ChevronLeft,
  FileText,
  Calendar,
  Briefcase,
  PieChart,
  PanelLeft,
  PanelTop,
  Package,
  Bookmark,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useListNotifications } from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";

const navItems = [
  { href: "/member/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { href: "/member/search", icon: Search, label: "Find Care" },
  { href: "/member/household", icon: Heart, label: "My Household" },
  { href: "/member/care-team", icon: Users, label: "Care Team" },
  { href: "/member/appointments", icon: Calendar, label: "Appointments" },
  {
    href: "/member/cost-estimates",
    icon: Calculator,
    label: "Good Faith Estimates",
  },
  { href: "/member/estimates", icon: FileText, label: "Estimates" },
  { href: "/member/bundles", icon: Package, label: "Bundles" },
  { href: "/member/reports", icon: PieChart, label: "Reports" },
  { href: "/member/membership-card", icon: CreditCard, label: "My Card" },
  { href: "/member/agents", icon: Briefcase, label: "Agent" },
  { href: "/member/settings", icon: Settings, label: "Settings" },
  { href: "/member/notifications", icon: Bell, label: "Notifications" },
];

export default function MemberLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const [location, setLocation] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navMode, setNavMode] = useState<"left" | "top">(() => {
    return (
      (localStorage.getItem("member_nav_mode") as "left" | "top") || "left"
    );
  });
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("member_nav_collapsed") === "true";
  });

  const { data: notifications } = useListNotifications();
  const unread = notifications?.filter((n) => !n.read).length ?? 0;

  const [user, setUser] = useState<any>(() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  });

  const toggleNavMode = (mode: "left" | "top") => {
    setNavMode(mode);
    localStorage.setItem("member_nav_mode", mode);
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("member_nav_collapsed", String(next));
      return next;
    });
  };

  useEffect(() => {
    const u = localStorage.getItem("user");
    console.log(
      "[MemberLayout] Checking user in localStorage:",
      u ? "EXISTS" : "MISSING",
    );
    if (u) {
      try {
        const parsedUser = JSON.parse(u);
        console.log(
          "[MemberLayout] Parsed user role:",
          parsedUser.role,
          "isImpersonating:",
          parsedUser.isImpersonating,
        );
        if (parsedUser.role !== "MEMBER") {
          console.warn(
            "[MemberLayout] Role mismatch! Role is:",
            parsedUser.role,
            "Redirecting...",
          );
          if (parsedUser.role === "PROVIDER" || parsedUser.role === "CLINIC") {
            setLocation("/provider/dashboard");
          } else if (parsedUser.role === "AGENT") {
            setLocation("/agent/dashboard");
          } else {
            setLocation("/login");
          }
          return;
        }
        setUser(parsedUser);
      } catch (e) {
        console.error(
          "[MemberLayout] Failed to parse user from localStorage:",
          e,
        );
      }
    } else {
      console.warn(
        "[MemberLayout] No user found in localStorage, redirecting to /login",
      );
      setLocation("/login");
    }
  }, [setLocation]);

  const displayName =
    user?.displayName ||
    (user?.firstName ? `${user.firstName} ${user.lastName}` : "Member");
  const fallbackChars = displayName.substring(0, 2).toUpperCase();

  const isActive = (href: string) => {
    if (href === "/member/estimates" || href === "/member/saved-estimates") {
      return (
        location === "/member/estimates" ||
        location.startsWith("/member/estimates/") ||
        location === "/member/saved-estimates" ||
        location.startsWith("/member/saved-estimates/")
      );
    }
    if (href === "/member/bundles" || href === "/member/saved-bundles") {
      return (
        location === "/member/bundles" ||
        location.startsWith("/member/bundles/") ||
        location === "/member/saved-bundles" ||
        location.startsWith("/member/saved-bundles/")
      );
    }
    return location === href || location.startsWith(href + "/");
  };

  const handleSignOut = () => {
    queryClient.clear();
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setLocation("/");
  };

  const SidebarNav = ({
    onItemClick,
    isMobile = false,
  }: {
    onItemClick?: () => void;
    isMobile?: boolean;
  }) => {
    const isCollapsed = isMobile ? false : collapsed;
    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="flex items-center justify-between px-4 py-5 flex-shrink-0 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-md shadow-primary/30">
              <Heart className="w-4 h-4 text-white" />
            </div>
            {!isCollapsed && (
              <span className="font-bold text-base text-foreground tracking-tight">
                StraightDoc
              </span>
            )}
          </div>
          {!isMobile && (
            <button
              type="button"
              onClick={toggleCollapsed}
              className="p-1 text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent transition"
              title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {collapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <ChevronLeft className="w-4 h-4" />
              )}
            </button>
          )}
        </div>

        {!isCollapsed && (
          <div className="px-3 pt-3">
            <div className="p-1 bg-muted/60 rounded-xl flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
              <button
                type="button"
                onClick={() => toggleNavMode("left")}
                className={`flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded-lg transition ${
                  navMode === "left"
                    ? "bg-background shadow-2xs text-primary font-bold"
                    : "hover:text-foreground"
                }`}
              >
                <PanelLeft className="w-3 h-3" /> Left
              </button>
              <button
                type="button"
                onClick={() => toggleNavMode("top")}
                className={`flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded-lg transition ${
                  navMode === "top"
                    ? "bg-background shadow-2xs text-primary font-bold"
                    : "hover:text-foreground"
                }`}
              >
                <PanelTop className="w-3 h-3" /> Top
              </button>
            </div>
          </div>
        )}

        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto min-h-0">
          {navItems.map((item) => {
            const active = isActive(item.href);
            return (
              <Link key={item.href} href={item.href} onClick={onItemClick}>
                <div
                  className={`flex items-center ${isCollapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"} rounded-2xl cursor-pointer transition-all group ${
                    active
                      ? "bg-primary text-primary-foreground font-semibold shadow-sm shadow-primary/25"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                  title={isCollapsed ? item.label : undefined}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!isCollapsed && (
                    <span className="text-xs font-medium flex-1">
                      {item.label}
                    </span>
                  )}
                  {!isCollapsed &&
                    item.label === "Notifications" &&
                    unread > 0 && (
                      <Badge className="text-[10px] px-1.5 h-4 min-w-0 bg-primary-foreground text-primary font-bold">
                        {unread}
                      </Badge>
                    )}
                </div>
              </Link>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-border flex-shrink-0">
          <div
            className={`flex items-center gap-3 px-2 py-2 rounded-2xl hover:bg-accent transition-colors cursor-pointer ${isCollapsed ? "justify-center" : ""}`}
          >
            <Avatar className="w-7 h-7 flex-shrink-0">
              <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-xs font-bold">
                {fallbackChars}
              </AvatarFallback>
            </Avatar>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{displayName}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {user?.role || "Member"}
                </p>
              </div>
            )}
          </div>
          <div
            onClick={handleSignOut}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-2xl cursor-pointer text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors mt-1 ${isCollapsed ? "justify-center" : ""}`}
            title={isCollapsed ? "Sign Out" : undefined}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!isCollapsed && (
              <span className="text-xs font-medium">Sign Out</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background text-foreground">
      {/* Top Navigation Mode Bar (REDESIGNED 2-ROW HEADER MATCHING ADMIN PORTAL) */}
      {navMode === "top" && (
        <header className="shrink-0 z-30 bg-card border-b border-border shadow-2xs">
          {/* Row 1: Brand, Mobile Hamburger, Layout Selector, User Info */}
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4 border-b border-border">
            <div className="flex items-center gap-3 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(true)}
                className="lg:hidden"
                title="Open navigation menu"
              >
                <Menu className="w-5 h-5 text-foreground" />
              </Button>
              <div className="w-8 h-8 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-sm text-foreground">
                StraightDoc
              </span>
              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider border-l border-border pl-3 hidden sm:inline">
                Member Portal
              </span>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <div className="p-1 bg-muted/60 rounded-xl flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hidden sm:flex">
                <button
                  type="button"
                  onClick={() => toggleNavMode("left")}
                  className="flex items-center gap-1.5 py-1 px-2.5 rounded-lg hover:text-foreground transition"
                >
                  <PanelLeft className="w-3.5 h-3.5" /> Left Nav
                </button>
                <button
                  type="button"
                  onClick={() => toggleNavMode("top")}
                  className="flex items-center gap-1.5 py-1 px-2.5 rounded-lg bg-background shadow-2xs text-primary font-bold"
                >
                  <PanelTop className="w-3.5 h-3.5" /> Top Nav
                </button>
              </div>

              <div className="flex items-center gap-2 pl-2 border-l sm:border-l border-border">
                <Avatar className="w-7 h-7">
                  <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-xs font-bold">
                    {fallbackChars}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs font-semibold text-foreground hidden md:inline">
                  {displayName}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSignOut}
                title="Sign Out"
              >
                <LogOut className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          </div>

          {/* Row 2: Centered Sub-Navigation Items (Desktop only) */}
          <div className="hidden lg:flex max-w-[1600px] mx-auto px-4 sm:px-6 py-2.5 items-center justify-center">
            <nav className="flex flex-wrap items-center justify-center gap-1.5 py-0.5 px-0 max-w-full">
              {navItems.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link key={item.href} href={item.href}>
                    <div
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap cursor-pointer transition-all shrink-0 ${
                        active
                          ? "bg-primary text-primary-foreground shadow-2xs"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      }`}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                      <span>{item.label}</span>
                    </div>
                  </Link>
                );
              })}
            </nav>
          </div>
        </header>
      )}

      {/* Main Body */}
      <div className="flex flex-1 overflow-hidden relative min-h-0">
        {navMode === "left" && (
          <aside
            className={`hidden lg:flex flex-col bg-card border-r border-border transition-all duration-300 h-full shrink-0 ${
              collapsed ? "w-20" : "w-60"
            }`}
          >
            <SidebarNav />
          </aside>
        )}

        {/* Mobile Top Bar */}
        {navMode === "left" && (
          <header className="lg:hidden h-14 bg-card border-b border-border flex items-center justify-between px-4 fixed top-0 left-0 right-0 z-20">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileOpen(true)}
                className="w-9 h-9 rounded-2xl flex items-center justify-center hover:bg-accent transition-colors"
                title="Open navigation menu"
              >
                <Menu className="w-5 h-5 text-muted-foreground" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Heart className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="font-bold text-sm text-foreground">
                  StraightDoc
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-xs font-bold">
                  {fallbackChars}
                </AvatarFallback>
              </Avatar>
            </div>
          </header>
        )}

        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 z-40 lg:hidden"
                onClick={() => setMobileOpen(false)}
              />
              <motion.aside
                initial={{ x: -260 }}
                animate={{ x: 0 }}
                exit={{ x: -260 }}
                transition={{ type: "spring", damping: 28, stiffness: 260 }}
                className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border z-50 lg:hidden"
              >
                <button
                  onClick={() => setMobileOpen(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-accent flex items-center justify-center text-muted-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
                <SidebarNav
                  onItemClick={() => setMobileOpen(false)}
                  isMobile={true}
                />
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        <main
          className={`flex-1 overflow-y-auto min-w-0 h-full ${navMode === "left" ? "pt-14 lg:pt-0" : ""}`}
        >
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="px-4 sm:px-6 md:px-8 py-6 max-w-[1600px] mx-auto w-full"
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
